import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ImageWithBasePath from "../../core/data/img/ImageWithBasePath";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { useStore } from "../../app/stores/store";
import { loginSchema, LoginSchema } from "../../lib/schemas/loginSchema";
import handleErrors from "../../lib/utils";
import { observer } from "mobx-react-lite";
import { all_routes } from "../router/all_routes";
import { Loader2, Mail, Lock, Eye, EyeOff } from "lucide-react";

// Components
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";
import Header from "../common/header";
import Footer from "../common/footer";

const routes = all_routes;

const Login = () => {
  const { userStore } = useStore();
  const navigate = useNavigate();
  const [summaryErrors, setSummaryErrors] = useState<string[] | null>(null);
  const [passwordVisible, setPasswordVisible] = useState(false);

  const form = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    mode: "onTouched",
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = form;

  const onSubmit = async (data: LoginSchema) => {
    const result = await userStore.login(data);

    if (result.status === "success") {
      toast.success("Login successful");

      const user = result.data;
      const isPartner = userStore.user?.isPartner === true;
      const isApproved = userStore.user?.partnerApproved === true;

      if (isPartner) {
        if (isApproved) {
          navigate(routes.PartnerHome);
        } else {
          toast.error("Your partner account is under review.");
        }
      } else {
        navigate(routes.homeOne);
      }
    } else {
      setSummaryErrors(handleErrors(result.error));
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Header />
      <main className="flex-grow pt-24 md:pt-32 flex items-center justify-center py-8">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Image */}
            <div className="hidden lg:block relative h-[600px] rounded-xl overflow-hidden">
              <img
                src="https://picsum.photos/seed/loginbg/800/600"
                alt="Abstract background image with colorful shapes and app screenshots"
                className="w-full h-full object-cover rounded-xl"
                loading="eager"
                data-ai-hint="colorful abstract background"
              />
            </div>

            {/* Right Side - Login Form */}
            <div className="flex items-center justify-center">
              <div className="w-full max-w-md">
                <Card className="border-black border-0 ">
                  <CardHeader className="text-center pb-4">
                    <div className="flex justify-center mb-4">
                      <ImageWithBasePath
                        lazyLoad={true}
                        src="assets/img/new updated logo-01.png"
                        alt="Logo"
                        className="h-12"
                      />
                    </div>
                    <CardTitle className="text-2xl font-bold text-gray-900">
                      Welcome Back
                    </CardTitle>
                    <p className="text-gray-600 mt-2">
                      Sign in to your account to continue
                    </p>
                  </CardHeader>

                  <CardContent className="space-y-4 ">
                    <Form {...form}>
                      <form
                        onSubmit={handleSubmit(onSubmit)}
                        className="space-y-4"
                      >
                        {/* Email Field */}
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <div className="relative flex items-center">
                            {/* <Mail className="absolute left-3 h-4 w-4 text-gray-400 pointer-events-none" /> */}
                            <Input
                              type="email"
                              placeholder="you@example.com"
                              {...register("email")}
                              className="pl-10 pr-3 focus-visible:ring-[#e71875] focus-visible:border-[#e71875]"
                            />
                          </div>
                          <FormMessage className="text-red-500" />
                        </FormItem>

                        {/* Password Field */}
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <div className="relative">
                            {/* <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" /> */}
                            <Input
                              type={passwordVisible ? "text" : "password"}
                              placeholder="••••••••"
                              {...register("password")}
                              className="pl-10 pr-10 focus-visible:ring-[#e71875] focus-visible:border-[#e71875]"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 hover:bg-transparent"
                              onClick={() =>
                                setPasswordVisible(!passwordVisible)
                              }
                            >
                              {passwordVisible ? (
                                <EyeOff className="h-4 w-4 text-gray-400" />
                              ) : (
                                <Eye className="h-4 w-4 text-gray-400" />
                              )}
                            </Button>
                          </div>
                          <FormMessage className="text-red-500" />
                        </FormItem>

                        {/* Error Messages */}
                        {summaryErrors && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            {summaryErrors.map((error, index) => (
                              <div key={index} className="text-red-600 text-sm">
                                {error}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Submit Button */}
                        <Button
                          type="submit"
                          disabled={isSubmitting || !isValid}
                          className="w-full bg-primary hover:bg-primary/90 text-white py-6"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Signing In...
                            </>
                          ) : (
                            "Sign In"
                          )}
                        </Button>
                      </form>
                    </Form>

                    {/* Registration Links */}
                    <div className="text-center pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-600 mb-3">
                        Don&apos;t have an account? Register as:
                      </p>
                      <div className="flex flex-col gap-2">
                        <Link
                          to={routes.registercustomer}
                          className="text-primary hover:text-primary/80 font-medium text-sm"
                        >
                          Customer
                        </Link>
                        <Link
                          to={routes.partnerRegister}
                          className="text-primary hover:text-primary/80 font-medium text-sm"
                        >
                          Partner
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default observer(Login);
